/**
 * TanStack Query hooks for project state management (Opus Build surface).
 *
 * Hooks:
 *   - useProjects: infinite paginated project list
 *   - useProject: single project detail (refetch on focus)
 *   - useCreateProject: mutation to create project
 *   - useArchiveProject: mutation to archive project
 */

import {
  useInfiniteQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  listProjects,
  getProject,
  createProject,
  updateProject,
  archiveProject,
  importProjectFromGitHub,
  publishProject,
  getPublishStatus,
  getPublishOfferings,
  getPublishInstances,
  publishInstanceAction,
  unpublishProject,
} from '@/services/api/projects';
import type {
  PublishRequest,
  PublishState,
  PublishOffering,
  PublishInstanceAction,
  PublishInstancesResponse,
} from '@/services/api/projects';
import type {
  ListProjectsParams,
  ProjectsPage,
  CreateProjectInput,
  UpdateProjectInput,
  ImportProjectResponse,
} from '@/types/code.types';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (params?: ListProjectsParams) =>
    [...projectKeys.lists(), params ?? {}] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  publishStatus: (id: string) => [...projectKeys.detail(id), 'publish-status'] as const,
  publishInstances: (id: string) => [...projectKeys.detail(id), 'publish-instances'] as const,
};

// ---------------------------------------------------------------------------
// useProjects -- infinite query for the project sidebar list
// ---------------------------------------------------------------------------

export function useProjects(
  params?: Omit<ListProjectsParams, 'cursor'>,
  /** When provided, enables periodic refetching at this interval (ms).
   *  Used by the sidebar to poll while a turn is active so background
   *  projects' current_stream updates within seconds. */
  refetchInterval?: number,
) {
  return useInfiniteQuery({
    queryKey: projectKeys.list(params),
    queryFn: ({ pageParam }) =>
      listProjects({ ...params, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ProjectsPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval,
  });
}

// ---------------------------------------------------------------------------
// useProject -- single project detail with refetch on focus
// ---------------------------------------------------------------------------

export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: projectKeys.detail(projectId ?? ''),
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// useCreateProject -- mutation
// ---------------------------------------------------------------------------

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateProjectInput) => createProject(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateProject -- mutation
// ---------------------------------------------------------------------------

export function useUpdateProject(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateProjectInput) => updateProject(projectId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// useArchiveProject -- mutation
// ---------------------------------------------------------------------------

export function useArchiveProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => archiveProject(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// useImportProject -- mutation
// ---------------------------------------------------------------------------

export interface ImportProjectInput {
  repoFullName: string;
  projectName?: string;
}

export function useImportProject() {
  const queryClient = useQueryClient();

  return useMutation<ImportProjectResponse, Error, ImportProjectInput>({
    mutationFn: ({ repoFullName, projectName }) =>
      importProjectFromGitHub(repoFullName, projectName),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// VibeHosting publish
// ---------------------------------------------------------------------------

/**
 * Poll the project's publish state. Auto-refreshes every 5s while a publish
 * (building/provisioning) or an unpublish (removing) is in flight; idle
 * otherwise.
 */
export function usePublishStatus(projectId: string | undefined) {
  return useQuery<PublishState>({
    queryKey: projectKeys.publishStatus(projectId ?? ''),
    queryFn: () => getPublishStatus(projectId!),
    enabled: !!projectId,
    refetchInterval: (query) => {
      const s = query.state.data?.publish_status;
      return s === 'analyzing' || s === 'building' || s === 'provisioning' || s === 'removing'
        ? 5000
        : false;
    },
  });
}

/**
 * List the host VM sizes (compute offerings) the user can pick from for a
 * publish. Org-scoped and rarely changes, so cache generously.
 */
export function usePublishOfferings() {
  return useQuery<PublishOffering[]>({
    queryKey: ['publish', 'offerings'],
    queryFn: getPublishOfferings,
    staleTime: 5 * 60 * 1000,
  });
}

/** Start a publish for a project. */
export function usePublishProject(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation<PublishState, Error, PublishRequest>({
    mutationFn: (body) => publishProject(projectId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(projectKeys.publishStatus(projectId), data);
      void queryClient.invalidateQueries({ queryKey: projectKeys.publishStatus(projectId) });
    },
  });
}

/**
 * List the VMs backing the current publish. Polls every 15s while enabled
 * (the Production tab keeps it enabled only when the app is published).
 */
export function usePublishInstances(projectId: string | undefined, enabled: boolean) {
  return useQuery<PublishInstancesResponse>({
    queryKey: projectKeys.publishInstances(projectId ?? ''),
    queryFn: () => getPublishInstances(projectId!),
    enabled: !!projectId && enabled,
    refetchInterval: 15_000,
  });
}

/** Start/stop one instance of the published app. */
export function usePublishInstanceAction(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { vmId: string; action: PublishInstanceAction }>({
    mutationFn: ({ vmId, action }) => publishInstanceAction(projectId, vmId, action),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.publishInstances(projectId) });
    },
  });
}

/** Unpublish (tear down) the published app. Status transitions to 'removing'. */
export function useUnpublishProject(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: () => unpublishProject(projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.publishStatus(projectId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.publishInstances(projectId) });
    },
  });
}
